import { useState } from "react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";

/**
 * ConvertToClientDialog — promotes a lead into a paying client.
 * The consultant picks a package type; conversion is one API call.
 */
export default function ConvertToClientDialog({ lead, packages, onClose, onConverted }) {
  const [pkg, setPkg] = useState("full_package");
  const [saving, setSaving] = useState(false);

  async function convert() {
    setSaving(true);
    try {
      const { data } = await api.post(`/crm/leads/${lead.id}/convert-to-client`, {
        package_type: pkg,
      });
      const pkgName = packages.find((p) => p.key === pkg)?.name || pkg;
      toast.success(`${lead.org_name} converted to client · ${pkgName}`);
      onConverted(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conversion failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="convert-to-client-dialog">
        <DialogHeader>
          <DialogTitle>Convert to client</DialogTitle>
          <DialogDescription>
            {lead.org_name} · {lead.contact_name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <label className="text-xs uppercase tracking-[0.2em] text-slate-500">
            Package
          </label>
          <Select value={pkg} onValueChange={setPkg}>
            <SelectTrigger data-testid="convert-package-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {packages.map((p) => (
                <SelectItem key={p.key} value={p.key} data-testid={`pkg-${p.key}`}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 pt-2">
            The client will start at the <strong>Onboarding</strong> stage in the client pipeline.
            You can then create their assessment and generate documents.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={convert}
            disabled={saving}
            className="bg-[#1A7F6E] hover:bg-[#136053] text-white"
            data-testid="convert-confirm-button"
          >
            {saving ? "Converting…" : "Convert to client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
